import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, TrendUp, ShieldCheck, Clock, Diamond } from "@phosphor-icons/react";

function Cell({ label, value }) {
  return (
    <div className="bg-[#0F1F38] border border-white/5 rounded-md p-5">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
        {label}
      </div>
      <p className="text-sm text-[#F5F1E8] leading-relaxed">{value}</p>
    </div>
  );
}

function OfferColumn({ offer, side }) {
  if (!offer) {
    return (
      <div className="bg-[#0F1F38] border border-dashed border-white/10 rounded-lg p-10 text-center text-[#808A9B] min-h-[400px] flex items-center justify-center">
        Pick an offer to compare on the {side} side.
      </div>
    );
  }
  const o = offer.output;
  return (
    <div className="space-y-4">
      <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-6">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
          Headline
        </div>
        <h3 className="font-display font-bold text-xl text-white leading-snug">
          {o.headline}
        </h3>
        <p className="mt-3 text-sm text-[#9AA5B5] italic">“{o.hook}”</p>
      </div>

      {o.proof && (
        <div
          className="bg-[#0F1F38] border border-[#C9A961]/30 rounded-lg p-6"
          data-testid={`compare-value-score-${side}`}
        >
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
            Value score vs the original offer
          </div>
          <div className="flex items-baseline gap-3">
            <span className="text-[#5A6577] font-display text-lg line-through">
              {o.proof.value_score_before}
            </span>
            <span className="font-display text-3xl font-black text-[#C9A961]">
              {o.proof.value_score_after}
            </span>
            <span className="text-xs text-[#9AA5B5]">
              {o.proof.all_levers_moved
                ? "all four levers moved"
                : "not every lever moved"}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Cell label="Dream Outcome" value={o.value_equation.dream_outcome} />
        <Cell label="Perceived Likelihood" value={o.value_equation.perceived_likelihood} />
        <Cell label="Time Delay" value={o.value_equation.time_delay} />
        <Cell label="Effort & Sacrifice" value={o.value_equation.effort_and_sacrifice} />
      </div>

      <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-6">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-3">
          Bonus stack ({o.bonus_stack?.length || 0})
        </div>
        <ul className="space-y-2">
          {o.bonus_stack?.map((b, i) => (
            <li key={i} className="text-sm text-[#F5F1E8] flex justify-between gap-3">
              <span>{b.name}</span>
              <span className="font-mono text-[#C9A961] shrink-0">{b.perceived_value}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Cell label="Unconditional Guarantee" value={o.guarantees.unconditional} />
        <Cell label="Conditional" value={o.guarantees.conditional} />
        <Cell label="Anti-Guarantee" value={o.guarantees.anti_guarantee} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Cell label="Scarcity" value={o.scarcity} />
        <Cell label="Urgency" value={o.urgency} />
      </div>

      <div className="bg-gradient-to-br from-[#0F1F38] to-[#0F1F38] border border-[#C9A961]/30 rounded-lg p-6">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
          Pricing
        </div>
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs text-[#9AA5B5]">Total stack value</div>
            <div className="font-display font-black text-lg gold-text">
              {o.total_stack_value}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#9AA5B5]">You pay</div>
            <div className="font-display font-black text-2xl text-white">
              {o.suggested_price}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const aId = params.get("a");
  const bId = params.get("b");
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (aId) getOffer(aId).then(setA).catch(() => setErr("Left offer not found"));
    if (bId) getOffer(bId).then(setB).catch(() => setErr("Right offer not found"));
  }, [aId, bId]);

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-[#9AA5B5] hover:text-white inline-flex items-center gap-2 mb-8"
          data-testid="compare-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
          Side by side
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
          Compare <span className="font-black">two offers</span>.
        </h1>
        {err && (
          <p className="mt-4 text-sm text-[#ef4444]" data-testid="compare-error">
            {err}
          </p>
        )}

        <div className="grid grid-cols-12 gap-6 mt-10">
          <div className="col-span-12 md:col-span-6" data-testid="compare-left">
            <OfferColumn offer={a} side="left" />
            {a && (
              <div className="mt-3 text-right">
                <Link
                  to={`/offer/${a.id}`}
                  className="text-xs text-[#C9A961] hover:underline"
                >
                  Open offer A →
                </Link>
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-6" data-testid="compare-right">
            <OfferColumn offer={b} side="right" />
            {b && (
              <div className="mt-3 text-right">
                <Link
                  to={`/offer/${b.id}`}
                  className="text-xs text-[#C9A961] hover:underline"
                >
                  Open offer B →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
